import { useDebounceEffect } from 'ahooks';
import {
  CheckboxOptionType,
  Col,
  Input,
  Layout,
  Radio,
  RadioChangeEvent,
  Row,
  Spin,
  Table,
  Tag,
} from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type {
  StoredQualification,
  StoredUser,
  StoredUserWithQualification,
} from '../../hooks/types';
import useQueryQualification from '../../hooks/useQueryQualification';
import useQueryUser from '../../hooks/useQueryUser';
import { bringMatchingItemsToHead, userFullName } from '../../lib/utils';
import rfsTheme from '../../theme';
import routes, { routesWithPathParameter } from '../App/routes';
import HeaderSection from '../HeaderSection';
import { Optional } from '../Shared';
import TablePagination, {
  getTableSearchProps,
  TableSearchProps,
} from '../TablePagination';
import TypeAhead, { TypeAheadOption } from '../AccessibleComponents/TypeAhead';
import { onApolloError } from '../../lib/graphql/helpers';
import { useNotify } from '../../hooks/useNotify';

const searchDebounceMillis = 300;

const usersPerPage = 20;

const maxQualificationPreviewCount = 10;
const maxQualificationAutocompleteCount = 10;

type PersonnelNameWithId = {
  readonly id: string;
  readonly fullName: string;
};

type PersonnelTableRow = {
  readonly nameWithId: PersonnelNameWithId;
  readonly key: string;
  readonly qualifications: {
    readonly data: readonly StoredQualification[];
    readonly userId: string;
  };
} & StoredUser;

const searchModes = {
  qualification: 'qualification',
  nameOrEmail: 'nameOrEmail',
};

type SearchModeValue = typeof searchModes;

type SearchMode = keyof SearchModeValue;

const searchModeOptions: readonly CheckboxOptionType[] = [
  {
    label: 'Search by name or email',
    value: 'nameOrEmail',
  },
  {
    label: 'Search by qualification',
    value: 'qualification',
  },
];

type ViewPersonnelButtonProps = {
  readonly userId: string;
  readonly text: string;
};

const ViewPersonnelButton = ({ userId, text }: ViewPersonnelButtonProps) => (
  <Link
    className={`view-personnel-${userId}`}
    tabIndex={0}
    to={routesWithPathParameter(routes.personnel, 'userId', userId)}
    style={{ color: rfsTheme.errorTextColour }}
  >
    {text}
  </Link>
);

/**
 * Renders a page that provides personnel search of NSW RFS.
 */
const PersonnelSearch = () => {
  const { notify } = useNotify();

  // Note: these query state hooks are set by the debounce hook and shouldn't
  // be assigned directly by inputs
  //    Controls the query for populating the qualifications autocomplete drop down
  const [qualificationQuery, setQualificationQuery] = useState<string>('');
  //    Controls the user query when searching for users by free text search (e.g. name)
  const [userQuery, setUserQuery] = useState<string | undefined>(undefined);

  // Note: These hooks are set directly by inputs, and then are debounced to set the query state above
  //  Set by the "Search by email or name" text box
  const [userQueryInput, setUserQueryInput] = useState('');
  //  Set by the user typing into the qualifications autocomplete input
  const [qualificationQueryInput, setQualificationQueryInput] = useState('');

  // Set when a user selects a qualification in the auto complete drop down to filter to a specific qualification
  const [userQualificationIdFilter, setUserQualificationIdFilter] = useState<
    string | undefined
  >(undefined);
  // Populated as the user types into the auto complete drop down with the results of the qualificationQuery
  const [qualificationOptions, setQualificationOptions] = useState<
    readonly TypeAheadOption[]
  >([]);

  const [searchProps, setSearchProps] = useState<TableSearchProps>(
    getTableSearchProps(usersPerPage, 1)
  );

  const [searchMode, setSearchMode] = useState<SearchMode>('nameOrEmail');

  const [currentPage, setCurrentPage] = useState(1);

  const { data: userResult, loading: searchingPersonnel } = useQueryUser(
    searchProps.limit,
    searchProps.offset,
    onApolloError(notify.error),
    userQualificationIdFilter,
    userQuery
  );

  const { data: qualificationResult } = useQueryQualification(
    maxQualificationAutocompleteCount,
    0,
    qualificationQuery,
    onApolloError(notify.error)
  );

  const resetPage = () => {
    setCurrentPage(1);
    setSearchProps(getTableSearchProps(usersPerPage, 1));
  };

  useDebounceEffect(
    () => {
      setQualificationQuery(qualificationQueryInput);

      setUserQuery(userQueryInput === '' ? undefined : userQueryInput);

      resetPage();
    },
    [qualificationQueryInput, userQueryInput],
    {
      wait: searchDebounceMillis,
    }
  );

  const onUserQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserQueryInput(e.target.value);
  };

  const onSearchModeChange = useCallback((e: RadioChangeEvent) => {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const nextSearchMode = e.target.value as SearchMode;

    setSearchMode(nextSearchMode);

    setQualificationQueryInput('');

    setUserQueryInput('');

    resetPage();
  }, []);

  const onPageChange = useCallback((pageNumber: number) => {
    setCurrentPage(pageNumber);
    setSearchProps(getTableSearchProps(usersPerPage, pageNumber));
  }, []);

  const searchedUsers: readonly StoredUserWithQualification[] =
    searchMode === searchModes.qualification
      ? userResult?.user ?? []
      : userResult?.search_user ?? [];

  const personnelTableData: readonly PersonnelTableRow[] = searchedUsers.map(
    (user) => ({
      ...user,
      key: user.id,
      qualifications: {
        data: user.group_membership.flatMap(
          (membership) => membership.group.qualifications
        ),
        userId: user.id,
      },
      nameWithId: {
        id: user.id,
        fullName: userFullName(user),
      },
    })
  );

  const totalUsers =
    searchMode === searchModes.qualification
      ? userResult?.user_aggregate.aggregate?.count ?? 0
      : userResult?.search_user_aggregate.aggregate?.count ?? 0;

  // Qualification AutoComplete effects

  // Update the AutoComplete options whenever the qualification query result changes
  useEffect(() => {
    // Only show the autocomplete drop down if the user has started typing
    // otherwise it will just show arbitrary qualifications
    if (qualificationQuery.length > 0) {
      setQualificationOptions(
        (qualificationResult?.qualification ?? []).map((qual) => ({
          label: `${qual.name} (${qual.abbrev ?? ''})`,
          value: `${qual.name} (${qual.abbrev ?? ''})`,
          id: qual.qualification_id,
        }))
      );
    } else {
      setQualificationOptions([]);
    }
  }, [qualificationResult, qualificationQuery]);

  // Qualification TypeAhead component handlers

  /**
   * Called when a user clicks the clear icon to the right of the qualifications drop down
   */
  const onQualificationClear = () => {
    setUserQualificationIdFilter(undefined);
  };

  /**
   * Called whenever the user types something into the qualifications auto complete box.
   * Will update the qualification query which will populate the auto complete drop down options.
   * @param searchText the qualification name or code to search with.
   */
  const onQualificationSearch = (searchText: string) => {
    setQualificationQueryInput(searchText);
  };

  /**
   * Called when a user selects a specific qualification from the auto complete drop down.
   * Will set the qualification to filter users by.
   * @param qualificationId the ID of the qualification that was selected.
   */
  const onQualificationSelect = (qualificationId: string) => {
    setUserQualificationIdFilter(qualificationId);
    resetPage();
  };

  return (
    <Layout>
      <HeaderSection title={'NSW RFS Personnel'} />
      <Row>
        <Col>
          <Radio.Group
            id="personnel-search-mode-radio-group"
            options={[...searchModeOptions]}
            onChange={onSearchModeChange}
            value={searchMode}
            optionType="button"
          />
        </Col>
      </Row>
      <Row gutter={16} style={{ marginTop: '10px' }}>
        <Optional show={searchMode === searchModes.qualification}>
          <Col span={6}>
            <TypeAhead
              ariaLabel="Search by qualification name"
              id="qualification-search"
              className="qualification-search"
              onSearch={onQualificationSearch}
              onSelect={onQualificationSelect}
              onClear={onQualificationClear}
              options={[...qualificationOptions]}
              placeholder="Enter qualification name or code here"
            />
          </Col>
        </Optional>
        <Optional show={searchMode === searchModes.nameOrEmail}>
          <Col span={6}>
            <Input
              id="user-search-input"
              placeholder="Enter user's name or email here"
              value={userQueryInput}
              onChange={onUserQueryChange}
              aria-label="Search by user name or email"
            />
          </Col>
        </Optional>
      </Row>
      <TablePagination
        currentPage={currentPage}
        total={totalUsers}
        itemsPerPage={usersPerPage}
        onPageChange={onPageChange}
      />
      <Table
        id="personnel-team-table"
        dataSource={personnelTableData}
        locale={{
          emptyText: searchingPersonnel ? (
            <Spin size="large" />
          ) : (
            'Search for personnel'
          ),
        }}
        columns={[
          {
            title: 'Name',
            dataIndex: 'nameWithId',
            key: 'name',
            render: (personnel: PersonnelNameWithId) => (
              <ViewPersonnelButton
                userId={personnel.id}
                text={personnel.fullName}
              />
            ),
          },
          {
            title: 'Qualifications',
            dataIndex: 'qualifications',
            key: 'qualifications',
            width: '50%',
            render: ({
              data,
              userId,
            }: {
              readonly data: readonly StoredQualification[];
              readonly userId: string;
            }) => (
              <>
                {bringMatchingItemsToHead(data, (item) =>
                  userQualificationIdFilter === undefined
                    ? true
                    : item.qualification_id === userQualificationIdFilter
                )
                  .slice(0, maxQualificationPreviewCount)
                  .map((qualification) => (
                    <Tag
                      id={`${userId}-qual-tag-${qualification.qualification_id}`}
                      key={qualification.qualification_id}
                      style={{
                        color: rfsTheme.darkRfsRed,
                      }}
                    >
                      {qualification.name} ({qualification.abbrev ?? ''})
                    </Tag>
                  ))}
                {data.length > maxQualificationPreviewCount ? (
                  <div style={{ marginTop: '10px' }}>
                    <ViewPersonnelButton userId={userId} text="View More" />
                  </div>
                ) : (
                  <></>
                )}
              </>
            ),
          },
          {
            title: 'User Type',
            dataIndex: 'user_type',
            key: 'userType',
          },
        ]}
        pagination={false}
        style={{ marginTop: '20px' }}
      />
    </Layout>
  );
};

export default PersonnelSearch;
